import { Children, ReactNode } from 'react';
import { useNavigate } from 'react-router-dom';
import { UseMutationResult } from '@tanstack/react-query';
import {
  Button,
  ButtonProps,
  DatePicker,
  Descriptions,
  Form,
  FormItemProps,
  FormProps,
  Input,
  ResultProps,
  Space,
} from 'antd';
import { LabeledValue } from 'antd/es/select';
import { AxiosResponse } from 'axios';
import dateFnsGenerateConfig from 'rc-picker/lib/generate/dateFns';

import { CreateNodeSupplierContractDtoExternal, NodeSupplierContractWithDetails } from '@app/domain-t3-interfaces';
import { Common } from '@app/web-pages';

import { NodePicker } from '../../nodes';

const DatePickerFns = DatePicker.generatePicker<Date>(dateFnsGenerateConfig);

export type NodeSupplierContractFormData = {
  supplierOption?: LabeledValue;
  supplierName?: string | null;
  node?: LabeledValue;
  contractName: string;
  contractNumber: string;
  intelSignedDate: Date;
};

export const useNodeSupplierContractForm = (
  mutate: UseMutationResult<
    AxiosResponse<NodeSupplierContractWithDetails, unknown>,
    unknown,
    CreateNodeSupplierContractDtoExternal,
    unknown
  >,
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  onCancel?: (e: any) => void,
) => {
  const [form] = Form.useForm<NodeSupplierContractFormData>();
  const navigate = useNavigate();

  const _getDescriptionItems = () => {
    const supplierOption = form.getFieldValue('supplierOption');
    const supplierName = form.getFieldValue('supplierName');
    const node = form.getFieldValue('node');
    const items = [{ key: 'node', children: node.label, label: 'Node' }];
    if (!supplierOption && !supplierName) return items;
    else if (!supplierOption && supplierName)
      return [{ key: 'supplier', children: supplierName, label: 'Supplier' }, ...items];
    const [externalId, name] = supplierOption.label.split('|');
    return [
      { key: 'supplier', children: name.trim(), label: 'Supplier' },
      { key: 'externalId', children: externalId.trim(), label: 'External ID' },
      ...items,
    ];
  };

  const getSupplierFormItemProps = (): FormItemProps => ({
    children: <Common.SupplierPicker.Select />,
    label: 'Supplier',
    name: 'supplierOption',
    required: true,
    rules: [{ required: true }],
  });

  const getNodeFormItemProps = (): FormItemProps => {
    return {
      children: <NodePicker labelInValue />,
      label: 'Node',
      name: 'node',
      required: true,
      rules: [{ required: true }],
    };
  };

  const getContractNameFormItemProps = (): FormItemProps => ({
    children: <Input />,
    label: 'Contract Name',
    name: 'contractName',
    required: true,
    rules: [{ required: true }],
  });

  const getContractNumberFormItemProps = (): FormItemProps => ({
    children: <Input />,
    label: 'Contract Number',
    name: 'contractNumber',
    required: true,
    rules: [{ required: true }],
  });

  const getIntelSignedDateFormItemProps = (): FormItemProps => ({
    children: <DatePickerFns />,
    label: 'Intel Signed Date',
    name: 'intelSignedDate',
    required: true,
    rules: [{ required: true }],
  });

  const getSaveButtonProps = (): ButtonProps => ({
    children: 'Save',
    htmlType: 'submit',
    loading: mutate.isPending,
    type: 'primary',
  });

  const getCancelButtonProps = (): ButtonProps => ({
    children: 'Cancel',
    onClick: onCancel ?? (() => navigate('../')),
  });

  const getResetButtonProps = (): ButtonProps => ({
    children: 'Reset',
    onClick: () => form.resetFields(),
  });

  const getCreateAnotherButtonProps = (): ButtonProps => ({
    children: 'Create Another',
    onClick: () => {
      mutate.reset();
      form.resetFields();
    },
  });

  const getGotoNodeSupplierContractsButtonProps = (): ButtonProps => ({
    children: 'Go to Node Supplier Contracts',
    type: 'primary',
    onClick: () => navigate('../'),
  });

  const getFormProps = (
    type: 'edit' | 'create' = 'create',
    initialValues?: NodeSupplierContractFormData,
  ): FormProps<NodeSupplierContractFormData> & { children: ReactNode } => ({
    initialValues: initialValues,
    children: (
      <>
        {type === 'create' ? (
          <>
            <Form.Item {...getSupplierFormItemProps()} />
            <Form.Item {...getNodeFormItemProps()} />
          </>
        ) : (
          <Form.Item dependencies={['supplierOption', 'supplierName', 'node']} preserve>
            {() => <Descriptions column={1} bordered items={_getDescriptionItems()} />}
          </Form.Item>
        )}

        <Form.Item {...getContractNameFormItemProps()} />
        <Form.Item {...getContractNumberFormItemProps()} />
        <Form.Item {...getIntelSignedDateFormItemProps()} />
        <Space>
          <Button {...getSaveButtonProps()} />
          <Button {...getResetButtonProps()} />
          <Button {...getCancelButtonProps()} />
        </Space>
      </>
    ),
    form,
    layout: 'vertical',
    onFinish: (formData) => {
      const externalSupplierId = Number(
        type === 'create' ? formData.supplierOption?.value : initialValues?.supplierOption?.value,
      );
      const nodeId = Number(type === 'create' ? formData.node?.value : initialValues?.node?.value);
      const createDto: CreateNodeSupplierContractDtoExternal = {
        supplierName: type === 'create' ? formData.supplierName : initialValues?.supplierName,
        nodeId: isNaN(nodeId) ? undefined : nodeId,
        externalSupplierId: isNaN(externalSupplierId) ? undefined : externalSupplierId,
        contractName: formData.contractName,
        contractNumber: formData.contractNumber,
        intelSignedDate: formData.intelSignedDate,
      };
      mutate.mutate(createDto);
    },
  });

  const getSuccessResultProps = (props?: ResultProps): ResultProps => ({
    status: 'success',
    subTitle: `Node-Supplier contract name: ${mutate.data?.data.contractName}`,
    ...props,
    extra: Children.toArray(props?.extra),
  });

  return {
    form,
    getCancelButtonProps,
    getCreateAnotherButtonProps,
    getFormProps,
    getGotoNodeSupplierContractsButtonProps,
    getSuccessResultProps,
  };
};
